var _excluded = ["variant", "onDismiss", "heading", "bodyText", "primaryAction", "secondaryAction", "collapseAt"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import { nanoid } from 'nanoid';
import React from 'react';
import styled from 'styled-components';
import { Bulb, CheckCircle, Close, Forbid, Information, WarningTriangle } from "../../../icons/index.js";
import Button from "../../basic/Button/Button.js";
import Icon from "../../basic/Icon/Icon.js";
import Layout from "../../basic/Layout/Layout.js";
import BodyText from "../../type/BodyText.js";
import Heading from "../../type/Heading.js";
import CallsToAction from "./CallsToAction.js";
var variants = {
  default: {
    value: 'default',
    icon: Information,
    background: 'var(--white)',
    borderColor: 'var(--gray-5)',
    role: 'status',
    darkModeBackground: 'var(--gray-7)',
    darkModeBorderColor: 'var(--white)'
  },
  guidance: {
    value: 'guidance',
    icon: Bulb,
    background: 'var(--blue-1)',
    borderColor: 'var(--blue-6)',
    role: 'status',
    darkModeBackground: 'var(--blue-8)',
    darkModeBorderColor: 'var(--blue-1)'
  },
  success: {
    value: 'success',
    icon: CheckCircle,
    background: 'var(--green-1)',
    borderColor: 'var(--green-6)',
    role: 'status',
    darkModeBackground: 'var(--green-8)',
    darkModeBorderColor: 'var(--green-1)'
  },
  warning: {
    value: 'warning',
    icon: WarningTriangle,
    background: 'var(--yellow-1)',
    borderColor: 'var(--yellow-6)',
    role: 'alert',
    darkModeBackground: 'var(--yellow-8)',
    darkModeBorderColor: 'var(--yellow-1)'
  },
  critical: {
    value: 'critical',
    icon: Forbid,
    background: 'var(--red-1)',
    borderColor: 'var(--red-6)',
    role: 'alert',
    darkModeBackground: 'var(--red-8)',
    darkModeBorderColor: 'var(--red-1)'
  }
};
var StyledBanner = styled.aside.withConfig({
  componentId: "CL__sc-1aipvnw-0"
})(["--banner-bkg-dark:", ";--banner-border-dark:", ";position:relative;border-radius:var(--border-radius-medium);padding:var(--spacing-2);background:", ";border:1px solid ", ";[data-theme='dark'] &{background:var(--banner-bkg-dark);border-color:var(--banner-border-dark);}"], _ref => {
  var {
    variant
  } = _ref;
  return variants[variant].darkModeBackground;
}, _ref2 => {
  var {
    variant
  } = _ref2;
  return variants[variant].darkModeBorderColor;
}, _ref3 => {
  var {
    variant
  } = _ref3;
  return variants[variant].background;
}, _ref4 => {
  var {
    variant
  } = _ref4;
  return variants[variant].borderColor;
});
var StyledLayout = styled(Layout).withConfig({
  componentId: "CL__sc-1aipvnw-1"
})(["flex:1;@media (max-width:", "){--layout-gap:6px;}"], _ref5 => {
  var {
    collapseAt
  } = _ref5;
  return "".concat(collapseAt, "px");
});
var StyledIcon = styled(Icon).withConfig({
  componentId: "CL__sc-1aipvnw-2"
})(["color:", ";[data-theme='dark'] &{color:var(--banner-border-dark);}"], _ref6 => {
  var {
    variant
  } = _ref6;
  return variants[variant || variants.default.value].borderColor;
});
var Content = styled.div.withConfig({
  componentId: "CL__sc-1aipvnw-3"
})(["max-width:636px;"]);
var StyledHeading = styled(Heading).withConfig({
  componentId: "CL__sc-1aipvnw-4"
})(["margin-bottom:0;"]);
var StyledBodyText = styled(BodyText).withConfig({
  componentId: "CL__sc-1aipvnw-5"
})(["margin-bottom:0;"]);
var StyledCloseButtonContainer = styled.div.withConfig({
  componentId: "CL__sc-1aipvnw-6"
})(["display:flex;padding-left:var(--spacing-6);@media (max-width:", "){& > button.inline-dismiss{height:36px;width:36px;border-radius:36px;padding:6px;span[role='img']{--icon-size:20px;}}}"], _ref7 => {
  var {
    collapseAt
  } = _ref7;
  return "".concat(collapseAt, "px");
});
var Actions = styled(Layout).withConfig({
  componentId: "CL__sc-1aipvnw-7"
})(["padding-left:var(--spacing-6);@media (min-width:", "){& + ", "{padding-left:0;}}@media (max-width:", "){display:none;}"], _ref8 => {
  var {
    collapseAt
  } = _ref8;
  return "".concat(collapseAt, "px");
}, StyledCloseButtonContainer, _ref9 => {
  var {
    collapseAt
  } = _ref9;
  return "".concat(collapseAt, "px");
});
var CollapsedActions = styled(Layout).attrs({
  align: 'right'
}).withConfig({
  componentId: "CL__sc-1aipvnw-8"
})(["margin-top:var(--spacing-3);display:none;@media (max-width:", "){display:flex;}"], _ref10 => {
  var {
    collapseAt
  } = _ref10;
  return "".concat(collapseAt, "px");
});
function validateProps(_ref11) {
  var {
    primaryAction,
    secondaryAction
  } = _ref11;
  if (!primaryAction && secondaryAction) {
    console.error('[Banner Component] A primary action is required when a secondary action is provided. Provide only a primary action when you only have one.');
  }
}
var Banner = /*#__PURE__*/React.forwardRef((_ref12, ref) => {
  var {
      variant = 'default',
      onDismiss,
      heading,
      bodyText,
      primaryAction,
      secondaryAction,
      collapseAt = 767
    } = _ref12,
    props = _objectWithoutProperties(_ref12, _excluded);
  validateProps({
    heading,
    primaryAction,
    secondaryAction
  });
  var id = nanoid();
  var headingId = "banner-".concat(id, "-heading");
  var bodyTextId = "banner-".concat(id, "-body-text");
  var mainElementId = heading ? headingId : bodyTextId;
  return /*#__PURE__*/React.createElement(StyledBanner, _extends({
    "aria-labelledby": mainElementId,
    "aria-describedby": mainElementId,
    ref: ref,
    tabIndex: 0,
    variant: variant
  }, props), /*#__PURE__*/React.createElement(Layout, {
    valign: "top"
  }, /*#__PURE__*/React.createElement(StyledLayout, {
    collapseAt: collapseAt,
    valign: "top"
  }, /*#__PURE__*/React.createElement(StyledIcon, {
    "aria-hidden": "true",
    component: variants[variant].icon,
    size: 24,
    variant: variant
  }), /*#__PURE__*/React.createElement(Content, {
    role: variants[variant].role
  }, heading && /*#__PURE__*/React.createElement(StyledHeading, {
    forwardedAs: "h1",
    id: headingId,
    size: "xx-small"
  }, heading), /*#__PURE__*/React.createElement(StyledBodyText, {
    color: "var(--text-color)",
    id: bodyTextId,
    size: "small"
  }, bodyText))), primaryAction && /*#__PURE__*/React.createElement(Actions, {
    collapseAt: collapseAt
  }, /*#__PURE__*/React.createElement(CallsToAction, {
    primaryAction: primaryAction,
    secondaryAction: secondaryAction
  })), onDismiss && /*#__PURE__*/React.createElement(StyledCloseButtonContainer, {
    collapseAt: collapseAt
  }, /*#__PURE__*/React.createElement(Button, {
    "aria-label": "Dismiss banner",
    className: "dismiss",
    icon: Close,
    monochrome: true,
    onClick: onDismiss,
    size: "small",
    variant: "text"
  }))), primaryAction && /*#__PURE__*/React.createElement(CollapsedActions, {
    collapseAt: collapseAt
  }, /*#__PURE__*/React.createElement(CallsToAction, {
    primaryAction: primaryAction,
    secondaryAction: secondaryAction
  })));
});
export default Banner;